import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { PublicLayout } from "@/components/PublicLayout";
import { LotusMark } from "@/components/Logo";
import {
  CLINIC,
  LOCATIONS,
  CATEGORY_META,
  CATEGORY_ORDER,
  IMAGES,
  categoryImage,
} from "@/data/clinic";
import { MapPin, Clock, ArrowRight, Leaf, Apple, Sparkles, Heart } from "lucide-react";

const CATEGORY_ICON = {
  "Homoeopathy & Integrated Wellness": Leaf,
  "Nutrition, Lifestyle & Health Coaching": Apple,
  "Skin & Aesthetic Treatments": Sparkles,
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [treatments, setTreatments] = useState([]);

  useEffect(() => {
    api.get("/treatments").then((res) => setTreatments(res.data)).catch(() => {});
  }, []);

  const grouped = CATEGORY_ORDER.map((cat) => ({
    category: cat,
    items: treatments.filter((t) => t.category === cat),
  })).filter((g) => g.items.length > 0);

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-brand-greenDark grain" />
        <img
          src="/lotus.png"
          alt=""
          aria-hidden="true"
          className="absolute right-[-4%] top-1/2 -translate-y-1/2 w-[72%] max-w-3xl opacity-[0.07] pointer-events-none select-none"
        />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24 w-full">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <div className="flex items-center gap-3 mb-6">
              <LotusMark size={30} />
              <span className="text-brand-gold tracking-[0.25em] text-xs uppercase">
                {CLINIC.tagline}
              </span>
            </div>
            <h1 className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Reviving health, <span className="italic text-brand-gold">naturally.</span>
            </h1>
            <p className="mt-6 text-brand-cream/85 text-lg max-w-xl font-light">
              Homoeopathy, nutrition and skin aesthetics under one integrated, person-centred
              approach — with clinics in London and Mumbai.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <button
                data-testid="hero-book-button"
                onClick={() => navigate("/book")}
                className="bg-brand-gold text-white rounded-full px-8 py-3.5 font-medium hover:bg-[#a2762f] transition-all flex items-center gap-2"
              >
                Book an Appointment <ArrowRight size={18} />
              </button>
              <a
                href="#services"
                className="border border-brand-cream/40 text-brand-cream rounded-full px-8 py-3.5 font-medium hover:bg-brand-cream/10 transition-all"
                data-testid="hero-services-button"
              >
                Explore Services
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section id="about" className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-brand-gold tracking-[0.2em] text-xs uppercase">Supporting you holistically</span>
            <h2 className="font-serif text-3xl sm:text-4xl text-brand-green mt-3">
              Looking at you. Understanding you.
            </h2>
            <p className="mt-5 text-brand-green/75 leading-relaxed">
              {CLINIC.practitioner} believes that true wellbeing comes from balance. Her integrated
              approach looks at the whole person — mind, body, nutrition, lifestyle and emotional
              wellbeing — not just isolated symptoms.
            </p>
            <div className="mt-7 grid grid-cols-2 gap-4">
              {["Whole-Person Approach", "Natural & Holistic", "Nutrition & Lifestyle", "Skin & Aesthetic Wellbeing"].map(
                (f) => (
                  <div key={f} className="flex items-center gap-3 text-brand-green/85 text-sm">
                    <Heart size={16} className="text-brand-gold shrink-0" /> {f}
                  </div>
                )
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src={IMAGES.homoeopathy} alt="Homoeopathy" className="rounded-2xl h-56 w-full object-cover" />
            <img src={IMAGES.skin} alt="Skin aesthetics" className="rounded-2xl h-56 w-full object-cover mt-8" />
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="bg-brand-surface/60 border-y border-brand-green/10 py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-brand-gold tracking-[0.2em] text-xs uppercase">Our Services</span>
            <h2 className="font-serif text-3xl sm:text-4xl text-brand-green mt-3">Treatments & Consultations</h2>
          </div>

          <div className="space-y-16">
            {grouped.map((g, gi) => {
              const Icon = CATEGORY_ICON[g.category] || Leaf;
              return (
                <div key={g.category} data-testid={`service-group-${gi}`}>
                  <div className="flex flex-col md:flex-row md:items-end gap-4 mb-8">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-full bg-brand-green/10 flex items-center justify-center shrink-0">
                        <Icon size={24} className="text-brand-green" />
                      </div>
                      <h3 className="font-serif text-2xl text-brand-green">{g.category}</h3>
                    </div>
                    <p className="text-brand-green/65 text-sm md:max-w-xl md:ml-auto">
                      {CATEGORY_META[g.category]?.blurb}
                    </p>
                  </div>

                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {g.items.map((t) => (
                      <motion.div
                        key={t.id}
                        whileHover={{ y: -6 }}
                        className="bg-white rounded-2xl border border-brand-green/10 overflow-hidden flex flex-col"
                        data-testid={`treatment-card-${t.id}`}
                      >
                        <div className="h-32 overflow-hidden">
                          <img src={t.image || categoryImage(t.category)} alt={t.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="p-5 flex flex-col flex-1">
                          <h4 className="font-serif text-lg text-brand-green">{t.name}</h4>
                          <p className="text-brand-green/60 text-sm mt-2 flex-1">{t.description}</p>
                          <div className="flex items-center justify-between mt-4 pt-4 border-t border-brand-green/10">
                            <span className="flex items-center gap-1.5 text-brand-green/70 text-sm">
                              <Clock size={14} /> {t.duration_minutes} min
                            </span>
                            <span className="font-serif text-brand-gold text-lg">
                              {t.currency}{t.price}
                            </span>
                          </div>
                          <button
                            onClick={() => navigate(`/book?treatment=${t.id}`)}
                            data-testid={`book-treatment-${t.id}`}
                            className="mt-4 w-full bg-brand-green/5 text-brand-green rounded-full py-2.5 text-sm font-medium hover:bg-brand-green hover:text-white transition-all"
                          >
                            Book this
                          </button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* LOCATIONS */}
      <section id="locations" className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-brand-gold tracking-[0.2em] text-xs uppercase">Where to find us</span>
          <h2 className="font-serif text-3xl sm:text-4xl text-brand-green mt-3">Our Clinics</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {LOCATIONS.map((loc) => (
            <div key={loc.id} className="bg-white rounded-2xl border border-brand-green/10 p-7" data-testid={`location-card-${loc.id}`}>
              <div className="flex items-center gap-2 text-xs text-brand-gold uppercase tracking-wider">
                <span>{loc.flag}</span> {loc.country}
              </div>
              <h3 className="font-serif text-xl text-brand-green mt-2 flex items-center gap-2">
                <MapPin size={18} className="text-brand-gold" /> {loc.name}
              </h3>
              <p className="text-brand-green/70 text-sm mt-3">{loc.address}</p>
              <div className="mt-4 pt-4 border-t border-brand-green/10 space-y-1.5">
                {loc.hours.map((h) => (
                  <p key={h} className="flex items-center gap-2 text-brand-green/70 text-sm">
                    <Clock size={13} className="text-brand-gold" /> {h}
                  </p>
                ))}
                <p className="text-brand-gold text-xs italic pt-1">{loc.note}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-8">
        <div className="bg-brand-green rounded-3xl px-8 py-16 text-center relative overflow-hidden">
          <div className="relative">
            <LotusMark size={44} className="mx-auto mb-5" />
            <h2 className="font-serif text-3xl sm:text-4xl text-white">Nourish your body. Elevate your life.</h2>
            <p className="text-brand-cream/80 mt-4 max-w-xl mx-auto">
              Because good health is a daily choice. Book your consultation today.
            </p>
            <button
              onClick={() => navigate("/book")}
              data-testid="cta-book-button"
              className="mt-8 bg-brand-gold text-white rounded-full px-9 py-3.5 font-medium hover:bg-[#a2762f] transition-all inline-flex items-center gap-2"
            >
              Book Appointment <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
